/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/
import { ReactElement, useEffect } from 'react';
import { useSessionContext } from '@exo/frontend-common-session-context';
import { gql } from '@apollo/client';
import {
  renderDefaultError,
  renderDefaultLoading,
  SmartComponentProps
} from '@exo/frontend-common-utils';
import { useCart } from '../../hooks/useCart';
import { MonetaryAmount } from '../../model/monetaryAmount';

export const MiniCartContainer = ({
  render,
  renderError = renderDefaultError,
  renderLoading = renderDefaultLoading
}: Props) => {
  const sessionContext = useSessionContext();
  const currentUrl = location.pathname;
  // cache-and-network: the effect below ignores results while `loading`, so a stale cached cart
  // is never written into the cross-tab shared session (that made tabs overwrite each other).
  const { data, loading, error } = useCart<MiniCartResponse>(
    { cartId: sessionContext.cartId!, fetchPolicy: 'cache-and-network' },
    MiniCartContainer.fragment
  );

  const updateCartCount = (ctx: CartSession) => {
    if (ctx?.cartId !== sessionContext.cartId || ctx?.cartCount !== sessionContext.cartCount) {
      sessionContext.set(ctx);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (error) {
    } else if (data?.me?.carts?.[0]?.lineItems) {
      updateCartCount({
        cartCount: data.me.carts[0].lineItems.map(li => li.quantity).reduce((a, b) => a + b, 0),
        cartId: data.me.carts[0].id
      });
    }
    const promoCodes = sessionContext.cartPromocodes;
    const hasNoCarts = !data?.me?.carts || data.me.carts.length === 0;
    const hasNoItems = data?.me?.carts?.[0] && (!data.me.carts[0].lineItems || data.me.carts[0].lineItems.length === 0);
    if((hasNoCarts || hasNoItems) && Array.isArray(promoCodes) && promoCodes.length > 0){
        sessionContext.set({cartPromocodes: []});
    }
  }, [JSON.stringify(data), loading, error]);

  if (loading) renderLoading();
  // if (error) renderError(error);

  // The session value is shared by all tabs, so showing it keeps every tab identical.
  // Fall back to this tab's fetched cart only until the session has a count.
  const fetchedCount = data?.me?.carts?.[0]?.lineItems
    ? data.me.carts[0].lineItems.map(li => li.quantity).reduce((a, b) => a + b, 0)
    : undefined;
  const cartCount =
    sessionContext.type === 'NONE'
      ? 0
      : typeof sessionContext.cartCount === 'number'
        ? sessionContext.cartCount
        : (fetchedCount ?? 0);

  return render({
    count: cartCount
  });
};

type Props = SmartComponentProps<{
  render: ({ count: number }) => ReactElement | null;
}>;

MiniCartContainer.fragment = gql`
  fragment MiniCart on CrtCart {
    id
    grandTotal {
      value
      currency
    }
    lineItems {
      id
      quantity
    }
  }
`;

type CartSession = {
  cartCount?: number;
  cartId?: string;
};

type MiniCartResponse = {
  id: string;
  grandTotal: MonetaryAmount;
  lineItems: {
    id: string;
    quantity: number;
  }[];
};
